/**
 * SchoolSelector Component
 *
 * A searchable dropdown component for selecting a school.
 * Used by supervisors to select which school they're assessing.
 */

import { useState, useEffect, useRef } from 'react';
import { schoolService } from '../services/supabase';

const SchoolSelector = ({
  value,
  onChange,
  label = 'Pilih Sekolah',
  placeholder = 'Cari sekolah...',
  required = true,
  error,
  disabled = false
}) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSchools = async () => {
    setLoading(true);
    setLoadError(null);
    const { schools: data, error: loadError } = await schoolService.getSchools();
    if (loadError) {
      setLoadError('Gagal memuat daftar sekolah');
      setSchools([]);
    } else {
      setSchools(data || []);
    }
    setLoading(false);
  };

  const filteredSchools = schools.filter(school =>
    (school.nama && school.nama.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (school.npsn && school.npsn.includes(searchQuery)) ||
    (school.kota && school.kota.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedSchool = schools.find(s => s.npsn === value || s.id === value);

  const handleSelect = (school) => {
    onChange(school.id); // Always pass UUID to match sekolah.id in database
    setSearchQuery('');
    setShowDropdown(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Selected School Display or Search Input */}
      <div
        className={`w-full px-4 py-3 border rounded-lg cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${error ? 'border-red-500' : 'border-gray-300'}`}
        onClick={() => !disabled && setShowDropdown(!showDropdown)}
      >
        {selectedSchool ? (
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-gray-900">{selectedSchool.nama}</span>
              <span className="text-sm text-gray-500 ml-2">(NPSN: {selectedSchool.npsn})</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{placeholder}</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {/* Search Input */}
          <div className="p-2 border-b sticky top-0 bg-white">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Ketik untuk mencari..."
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* School List */}
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
              Memuat...
            </div>
          ) : loadError ? (
            <div className="p-4 text-center">
              <p className="text-red-500 mb-2">{loadError}</p>
              <button
                onClick={loadSchools}
                className="text-blue-600 hover:text-blue-700 text-sm underline"
              >
                Coba lagi
              </button>
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="p-4 text-center text-gray-500">Tidak ada sekolah ditemukan</div>
          ) : (
            filteredSchools.map(school => (
              <div
                key={school.id}
                onClick={() => handleSelect(school)}
                className={`px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  (school.npsn === value || school.id === value) ? 'bg-blue-50' : ''
                }`}
              >
                <div className="font-medium text-gray-900">{school.nama}</div>
                <div className="text-sm text-gray-500">
                  NPSN: {school.npsn} {school.kota && `- ${school.kota}`}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default SchoolSelector;
